import { FirebaseFirestore } from '@capacitor-firebase/firestore';
import type {
  CollectionReference,
  DocumentReference,
  DocumentSnapshot,
  Query,
  QueryCompositeFilterConstraint,
  QueryConstraint,
  QueryEndAtConstraint,
  QueryFieldFilterConstraint,
  QueryLimitConstraint,
  QueryOrderByConstraint,
  QuerySnapshot,
  QueryStartAtConstraint,
} from './firestore-types';

// Re-export all types directly
export * from './firestore-types';

// Store snapshot listeners
const snapshotListeners = new Map<string, any>();

// Helper to convert Firebase SDK query constraints to Capacitor plugin format
function convertQueryConstraints(constraints: QueryConstraint[]): {
  compositeFilter?: QueryCompositeFilterConstraint;
  queryConstraints: any[];
} {
  const filterConstraints: QueryFieldFilterConstraint[] = [];
  const compositeConstraints: QueryCompositeFilterConstraint[] = [];
  const nonFilterConstraints: any[] = [];

  for (const constraint of constraints) {
    if (constraint.type === 'where') {
      filterConstraints.push(constraint as QueryFieldFilterConstraint);
    } else if (constraint.type === 'or' || constraint.type === 'and') {
      compositeConstraints.push(constraint as QueryCompositeFilterConstraint);
    } else if (constraint.type === 'orderBy') {
      nonFilterConstraints.push({
        type: 'orderBy',
        fieldPath: (constraint as QueryOrderByConstraint).fieldPath,
        directionStr: (constraint as QueryOrderByConstraint).directionStr,
      });
    } else if (constraint.type === 'limit' || constraint.type === 'limitToLast') {
      nonFilterConstraints.push({
        type: constraint.type,
        limit: (constraint as QueryLimitConstraint).limit,
      });
    } else if (constraint.type === 'startAt' || constraint.type === 'startAfter') {
      nonFilterConstraints.push({
        type: constraint.type,
        reference: (constraint as QueryStartAtConstraint).reference,
      });
    } else if (constraint.type === 'endAt' || constraint.type === 'endBefore') {
      nonFilterConstraints.push({
        type: constraint.type,
        reference: (constraint as QueryEndAtConstraint).reference,
      });
    }
  }

  let compositeFilter: QueryCompositeFilterConstraint | undefined;

  // Handle composite constraints (OR/AND)
  if (compositeConstraints.length > 0) {
    compositeFilter = compositeConstraints[0]; // Use the first composite constraint
  } else if (filterConstraints.length > 0) {
    // Default to AND for multiple where constraints
    compositeFilter = {
      type: 'and',
      queryConstraints: filterConstraints.map((c) => ({
        type: 'where',
        fieldPath: c.fieldPath,
        opStr: c.opStr,
        value: c.value,
      })),
    };
  }

  return {
    compositeFilter,
    queryConstraints: nonFilterConstraints,
  };
}

// Create a DocumentReference-like object
// Can accept db, collectionRef, or path segments
export function doc(dbOrCollectionRef: any, ...pathSegments: string[]): DocumentReference {
  // If first arg is a CollectionReference, use its path
  if (dbOrCollectionRef && typeof dbOrCollectionRef === 'object' && dbOrCollectionRef.path) {
    const collectionPath = dbOrCollectionRef.path;
    const path = pathSegments.length > 0 ? `${collectionPath}/${pathSegments.join('/')}` : collectionPath;
    return {
      id: pathSegments[pathSegments.length - 1] || path.split('/').pop() || '',
      path,
      parent: dbOrCollectionRef,
      type: 'document',
    } as DocumentReference;
  }

  // Otherwise treat as path segments
  const path = pathSegments.join('/');
  return {
    id: pathSegments[pathSegments.length - 1],
    path,
    parent: null,
    type: 'document',
  } as DocumentReference;
}

// Create a CollectionReference-like object
export function collection(db: any, ...pathSegments: string[]): CollectionReference {
  const path = pathSegments.join('/');
  return {
    id: pathSegments[pathSegments.length - 1],
    path,
    type: 'collection',
  } as CollectionReference;
}

// Query builder
export function query(collectionRef: CollectionReference | Query, ...queryConstraints: QueryConstraint[]): Query {
  const basePath = (collectionRef as any).path;
  const { compositeFilter, queryConstraints: nonFilterConstraints } = convertQueryConstraints(queryConstraints);

  return {
    type: 'query',
    path: basePath,
    compositeFilter,
    queryConstraints: nonFilterConstraints,
  } as Query;
}

// Where clause
export function where(
  fieldPath: string,
  opStr: '<' | '<=' | '==' | '>=' | '>' | '!=' | 'array-contains' | 'array-contains-any' | 'in' | 'not-in',
  value: any
): QueryFieldFilterConstraint {
  return {
    type: 'where',
    fieldPath,
    opStr,
    value,
  };
}

// OrderBy clause
export function orderBy(fieldPath: string, directionStr: 'asc' | 'desc' = 'asc'): QueryOrderByConstraint {
  return {
    type: 'orderBy',
    fieldPath,
    directionStr,
  };
}

// Limit clause
export function limit(limitCount: number): QueryLimitConstraint {
  return {
    type: 'limit',
    limit: limitCount,
  };
}

// OR composite filter
export function or(...constraints: (QueryFieldFilterConstraint | QueryCompositeFilterConstraint)[]): QueryCompositeFilterConstraint {
  return {
    type: 'or',
    queryConstraints: constraints,
  };
}

// AND composite filter
export function and(...constraints: (QueryFieldFilterConstraint | QueryCompositeFilterConstraint)[]): QueryCompositeFilterConstraint {
  return {
    type: 'and',
    queryConstraints: constraints,
  };
}

// Get document
export async function getDoc<T = any>(docRef: DocumentReference): Promise<DocumentSnapshot<T>> {
  const result = await FirebaseFirestore.getDocument({
    reference: docRef.path,
  });

  return {
    id: result.snapshot.id,
    path: result.snapshot.path,
    data: () => result.snapshot.data as T | null,
    exists: () => result.snapshot.data !== null,
    metadata: result.snapshot.metadata || { fromCache: false, hasPendingWrites: false },
  } as DocumentSnapshot<T>;
}

// Set document
export async function setDoc(docRef: DocumentReference, data: any, options?: { merge?: boolean }): Promise<void> {
  await FirebaseFirestore.setDocument({
    reference: docRef.path,
    data,
    merge: options?.merge || false,
  });
}

// Update document
export async function updateDoc(docRef: DocumentReference, data: any): Promise<void> {
  await FirebaseFirestore.updateDocument({
    reference: docRef.path,
    data,
  });
}

// Delete document
export async function deleteDoc(docRef: DocumentReference): Promise<void> {
  await FirebaseFirestore.deleteDocument({
    reference: docRef.path,
  });
}

// Get documents from collection
export async function getDocs<T = any>(queryRef: Query | CollectionReference): Promise<QuerySnapshot<T>> {
  const path = (queryRef as any).path;

  if ((queryRef as Query).compositeFilter || (queryRef as Query).queryConstraints) {
    const queryObj = queryRef as Query;
    const result = await FirebaseFirestore.getCollection({
      reference: path,
      compositeFilter: queryObj.compositeFilter,
      queryConstraints: queryObj.queryConstraints || [],
    });

    const docs = result.snapshots.map((snap) => ({
      id: snap.id,
      path: snap.path,
      data: () => snap.data as T,
      exists: () => snap.data !== null,
      metadata: snap.metadata || { fromCache: false, hasPendingWrites: false },
    }));

    return {
      empty: result.snapshots.length === 0,
      size: result.snapshots.length,
      docs,
      docChanges: () =>
        docs.map((doc, index) => ({
          type: 'added' as const,
          doc,
          oldIndex: -1,
          newIndex: index,
        })),
      forEach: (callback: (doc: DocumentSnapshot<T>) => void) => {
        docs.forEach(callback);
      },
    } as QuerySnapshot<T>;
  } else {
    const result = await FirebaseFirestore.getCollection({
      reference: path,
    });

    const docs = result.snapshots.map((snap) => ({
      id: snap.id,
      path: snap.path,
      data: () => snap.data as T,
      exists: () => snap.data !== null,
      metadata: snap.metadata || { fromCache: false, hasPendingWrites: false },
    }));

    return {
      empty: result.snapshots.length === 0,
      size: result.snapshots.length,
      docs,
      docChanges: () =>
        docs.map((doc, index) => ({
          type: 'added' as const,
          doc,
          oldIndex: -1,
          newIndex: index,
        })),
      forEach: (callback: (doc: DocumentSnapshot<T>) => void) => {
        docs.forEach(callback);
      },
    } as QuerySnapshot<T>;
  }
}

// OnSnapshot - handles both documents and queries
export function onSnapshot<T = any>(
  ref: DocumentReference | Query | CollectionReference,
  callback: (snapshot: DocumentSnapshot<T> | QuerySnapshot<T>) => void,
  onError?: (error: Error) => void
): () => void {
  // Check if it's a document reference
  if ((ref as DocumentReference).type === 'document') {
    const docRef = ref as DocumentReference;
    const callbackId = `doc_${docRef.path}_${Date.now()}`;

    FirebaseFirestore.addDocumentSnapshotListener({ reference: docRef.path }, (event, error) => {
      if (error) {
        onError?.(new Error(error));
      } else if (event) {
        callback({
          id: event.snapshot.id,
          path: event.snapshot.path,
          data: () => event.snapshot.data as T | null,
          exists: () => event.snapshot.data !== null,
          metadata: event.snapshot.metadata || { fromCache: false, hasPendingWrites: false },
        } as DocumentSnapshot<T>);
      }
    }).then((id) => {
      snapshotListeners.set(callbackId, id);
    });

    return () => {
      const id = snapshotListeners.get(callbackId);
      if (id) {
        FirebaseFirestore.removeSnapshotListener({ callbackId: id });
        snapshotListeners.delete(callbackId);
      }
    };
  }

  // It's a query or collection
  const queryRef = ref as Query | CollectionReference;
  const callbackId = `query_${(queryRef as any).path}_${Date.now()}`;
  const path = (queryRef as any).path;

  if ((queryRef as Query).compositeFilter || (queryRef as Query).queryConstraints) {
    const queryObj = queryRef as Query;
    FirebaseFirestore.addCollectionSnapshotListener(
      {
        reference: path,
        compositeFilter: queryObj.compositeFilter,
        queryConstraints: queryObj.queryConstraints || [],
      },
      (event, error) => {
        if (error) {
          onError?.(new Error(error));
        } else if (event) {
          const docs = event.snapshots.map((snap) => ({
            id: snap.id,
            path: snap.path,
            data: () => snap.data as T,
            exists: () => snap.data !== null,
            metadata: snap.metadata || { fromCache: false, hasPendingWrites: false },
          }));
          callback({
            empty: event.snapshots.length === 0,
            size: event.snapshots.length,
            docs,
            docChanges: () =>
              docs.map((doc, index) => ({
                type: 'added' as const,
                doc,
                oldIndex: -1,
                newIndex: index,
              })),
            forEach: (callback: (doc: DocumentSnapshot<T>) => void) => {
              docs.forEach(callback);
            },
          } as QuerySnapshot<T>);
        }
      }
    ).then((id) => {
      snapshotListeners.set(callbackId, id);
    });
  } else {
    FirebaseFirestore.addCollectionSnapshotListener({ reference: path }, (event, error) => {
      if (error) {
        onError?.(new Error(error));
      } else if (event) {
        const docs = event.snapshots.map((snap) => ({
          id: snap.id,
          path: snap.path,
          data: () => snap.data as T,
          exists: () => snap.data !== null,
          metadata: snap.metadata || { fromCache: false, hasPendingWrites: false },
        }));
        callback({
          empty: event.snapshots.length === 0,
          size: event.snapshots.length,
          docs,
          docChanges: () =>
            docs.map((doc, index) => ({
              type: 'added' as const,
              doc,
              oldIndex: -1,
              newIndex: index,
            })),
          forEach: (callback: (doc: DocumentSnapshot<T>) => void) => {
            docs.forEach(callback);
          },
        } as QuerySnapshot<T>);
      }
    }).then((id) => {
      snapshotListeners.set(callbackId, id);
    });
  }

  return () => {
    const id = snapshotListeners.get(callbackId);
    if (id) {
      FirebaseFirestore.removeSnapshotListener({ callbackId: id });
      snapshotListeners.delete(callbackId);
    }
  };
}

// Write batch
export function writeBatch(): {
  set: (docRef: DocumentReference, data: any, options?: { merge?: boolean }) => void;
  update: (docRef: DocumentReference, data: any) => void;
  delete: (docRef: DocumentReference) => void;
  commit: () => Promise<void>;
} {
  const operations: any[] = [];

  return {
    set(docRef: DocumentReference, data: any, options?: { merge?: boolean }) {
      operations.push({
        type: 'set',
        reference: docRef.path,
        data,
        options: { merge: options?.merge || false },
      });
    },
    update(docRef: DocumentReference, data: any) {
      operations.push({
        type: 'update',
        reference: docRef.path,
        data,
      });
    },
    delete(docRef: DocumentReference) {
      operations.push({
        type: 'delete',
        reference: docRef.path,
      });
    },
    async commit() {
      await FirebaseFirestore.writeBatch({ operations });
    },
  };
}

// Export a db-like object for compatibility
// Try to provide a real `db` instance on web (firebase/firestore), otherwise fall back to placeholder
let webDb: any = null;
if (typeof window !== 'undefined') {
  try {
    // Lazy-require to avoid bundling native-only code
    const appModule = require('firebase/app');
    const firestoreModule = require('firebase/firestore');

    const getApps = appModule.getApps;
    const initializeApp = appModule.initializeApp;

    if (getApps().length === 0) {
      const firebaseProdConfig = require('./firebase_config/firebase.production.config.json');
      const firebaseStagConfig = require('./firebase_config/firebase.staging.config.json');
      const firebaseConfig = process.env.REACT_APP_ENV === 'staging' ? firebaseStagConfig : firebaseProdConfig;
      initializeApp(firebaseConfig);
    }

    webDb = firestoreModule.getFirestore();
  } catch (e) {
    // Not running in a web environment or firebase web SDK not available
    console.warn('firebase/firestore not available in this environment', e);
  }
}

export const db =
  webDb ||
  {
    // placeholder for native/plugin usage — wrapper functions above call the Capacitor plugin directly
  };
